import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import { FC, memo, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  DeliveriesByCourseDay,
  DeliveriesByCourseResponse,
  DeliveryByCourseRow,
  ShipperSection,
} from 'src/entities/Report.entity';
import { usePageAutoSplit } from 'src/pages/reports/usePageAutoSplit';

import style from './print.module.css';

// DOM 実測による動的分割の上限 (A4 本体高さ) を超えない範囲で、初期分割の粒度として使う。
// 実測が発動しない Jest (jsdom) 下ではこの行数がそのまま保たれる。
export const ROWS_PER_PAGE = 45;

type Props = {
  data: DeliveriesByCourseResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  issueDate: string;
  issueTime: string;
};

type RawPage = {
  day: DeliveriesByCourseDay;
  section: ShipperSection;
  rows: DeliveryByCourseRow[];
};

export type PageInfo = RawPage & {
  pageInSection: number;
  totalInSection: number;
};

/**
 * 日境界 → 荷主境界 → ROWS_PER_PAGE 行ごとに初期ページ分割する (DOM 実測の初期状態)。
 * 行 0 件のセクションは表示しない。
 */
export function buildPages(days: DeliveriesByCourseDay[]): RawPage[] {
  const pages: RawPage[] = [];
  days.forEach((day) => {
    day.sections.forEach((section) => {
      if (section.rows.length === 0) return;
      const total = Math.max(1, Math.ceil(section.rows.length / ROWS_PER_PAGE));
      for (let i = 0; i < total; i += 1) {
        pages.push({
          day,
          section,
          rows: section.rows.slice(i * ROWS_PER_PAGE, (i + 1) * ROWS_PER_PAGE),
        });
      }
    });
  });
  return pages;
}

// 和式区切り（全角空白 U+3000）は eslint no-irregular-whitespace を避けるため Unicode escape を使う
const IDEOGRAPHIC_SPACE = '\u3000';

export function formatJapaneseDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${y}年${m}月${d}日${IDEOGRAPHIC_SPACE}配送`;
}

/** (pageInSection, totalInSection) を分割確定後に計算する。 */
function withPageNumbers(pages: RawPage[]): PageInfo[] {
  const totalByKey = new Map<string, number>();
  pages.forEach((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    totalByKey.set(key, (totalByKey.get(key) ?? 0) + 1);
  });
  const indexByKey = new Map<string, number>();
  return pages.map((p) => {
    const key = `${p.day.delivery_date}__${p.section.shipper_name}`;
    const idx = (indexByKey.get(key) ?? 0) + 1;
    indexByKey.set(key, idx);
    return { ...p, pageInSection: idx, totalInSection: totalByKey.get(key) ?? 1 };
  });
}

const DeliveryByCourseReportPresenter: FC<Props> = memo(({ data, isLoading, isError, issueDate, issueTime }) => {
  const initialPages = useMemo(() => buildPages(data?.days ?? []), [data]);

  const getRows = useCallback((page: RawPage) => page.rows, []);
  const replaceRows = useCallback(
    (page: RawPage, rows: DeliveryByCourseRow[]): RawPage => ({ ...page, rows }),
    [],
  );

  const { pages: adjustedPages, pageRefs } = usePageAutoSplit<RawPage, DeliveryByCourseRow>({
    initialPages,
    getRows,
    replaceRows,
  });

  const pages = useMemo(() => withPageNumbers(adjustedPages), [adjustedPages]);
  const totalAll = Math.max(pages.length, 1);

  return (
    <>
      <Helmet>
        <title>納品先別コースリスト</title>
      </Helmet>
      <div className={style.shell} role="region" aria-label="納品先別コースリスト プレビュー">
        <div className={style.toolbar}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => window.close()}
          >
            戻る
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            納品先別コースリスト
          </Typography>
          <Box className={style.toolbarRight}>
            <Typography variant="caption" color="text.secondary">全 {totalAll} ページ</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
            >
              印刷
            </Button>
          </Box>
        </div>

        <div className={style.body}>
          {isLoading && <div className={style.empty}>読み込み中...</div>}
          {isError && <div className={style.empty}>データの取得に失敗しました。</div>}
          {!isLoading && !isError && pages.length === 0 && (
            <div className={style.empty}>該当するデータはありません。</div>
          )}
          {pages.map((page, pageIdx) => (
            <div
              className={style.page}
              style={{ page: 'A4DeliveryByCourse' }}
              key={`${page.day.delivery_date}-${page.section.shipper_name}-${page.pageInSection}`}
              ref={(el) => { pageRefs.current[pageIdx] = el; }}
            >
              <div className={style.header}>
                <div className={style.title}>納品先別コースリスト</div>
                <div className={style.sub}>
                  {page.section.shipper_name}{IDEOGRAPHIC_SPACE}{formatJapaneseDate(page.day.delivery_date)}
                </div>
                <div className={style.meta}>
                  発行日付: {issueDate}<br />
                  発行時刻: {issueTime}<br />
                  ページ: {page.pageInSection} / {page.totalInSection}
                </div>
              </div>
              <table className={style.table}>
                <thead>
                  <tr>
                    <th style={{ width: '8%' }}>伝票数</th>
                    <th style={{ width: '20%' }}>運送会社名</th>
                    <th style={{ width: '14%' }}>ドライバー名</th>
                    <th style={{ width: '8%' }}>コース</th>
                    <th style={{ width: '6%' }}>回転</th>
                    <th>納品先名</th>
                    <th style={{ width: '14%' }}>エリア</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row, rowIdx) => (
                    <tr key={`${row.store_name}-${row.course_number ?? 'nil'}-${row.rotation ?? 'nil'}-${rowIdx}`}>
                      <td className={style.num}>{row.invoice_count || ''}</td>
                      <td>{row.carrier_company}</td>
                      <td>{row.driver_name}</td>
                      <td className={style.center}>{row.course_number ?? ''}</td>
                      <td className={style.center}>
                        {row.rotation !== null && row.rotation >= 2 ? row.rotation : ''}
                      </td>
                      <td>{row.store_name}</td>
                      <td>{row.area}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </>
  );
});

export default DeliveryByCourseReportPresenter;
